import { Controller } from "@hotwired/stimulus";

const WARNING_THRESHOLD = 300;
export default class extends Controller {
  static targets = ["counter", "error"];
  static values = { max: { type: Number, default: 3000 } };

  // Called when Stimulus attaches this controller to the form element.
  // Connects to the Trix editor, listens for text changes and form submissions.
  // Stores bound references so the same listener can be removed in disconnect().
  connect () {
    this.isOverLimit = false;
    this.boundUpdate = this.updateCount.bind(this);
    this.boundInit = this.initEditor.bind(this);
    this.boundPreventSubmit = this.preventOverLimitSubmit.bind(this);
    this.boundResetSubmitFlag = this.resetFormSubmitFlag.bind(this);

    this.element.addEventListener("submit", this.boundPreventSubmit);
    this.element.addEventListener("turbo:submit-end", this.boundResetSubmitFlag);

    // Trix is a web component that initializes asynchronously, so it may not
    // be ready when connect() fires. If it is, attach listeners immediately;
    // otherwise, wait for the trix-initialize event.
    this.editorElement = this.element.querySelector("trix-editor");
    if (this.editorElement && this.editorElement.editor) {
      this.attachListeners();
    } else {
      this.element.addEventListener("trix-initialize", this.boundInit, { once: true });
    }
  }

  // Removes all event listeners to prevent memory leaks when the controller
  // is torn down (e.g. during Turbo navigation or DOM replacement).
  disconnect () {
    if (this.editorElement) {
      this.editorElement.removeEventListener("trix-change", this.boundUpdate);
    }
    this.element.removeEventListener("trix-initialize", this.boundInit);
    this.element.removeEventListener("submit", this.boundPreventSubmit);
    this.element.removeEventListener("turbo:submit-end", this.boundResetSubmitFlag);
  }

  // Fallback callback for when the Trix editor wasn't ready during connect().
  // Fires once when trix-initialize triggers, then attaches listeners.
  initEditor (event) {
    this.editorElement = event.target;
    this.attachListeners();
  }

  // Starts listening for text changes in the Trix editor and runs an initial count.
  attachListeners () {
    this.editorElement.addEventListener("trix-change", this.boundUpdate);
    this.updateCount();
  }

  // Blocks form submission when the character count exceeds the limit.
  // stopImmediatePropagation prevents form_control_controller from marking the
  // form as submitted, which would incorrectly disable the unsaved-changes warning.
  preventOverLimitSubmit (event) {
    if (!this.isOverLimit) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = this.errorTarget.dataset.message;
      this.errorTarget.classList.remove("hidden");
    }
  }

  // After a failed server response, reset form_control_controller's submitted flag
  // so the beforeunload "unsaved changes" warning remains active.
  resetFormSubmitFlag (event) {
    if (event.detail.success) return;

    const formControl = this.application.getControllerForElementAndIdentifier(
      this.element, "form-control",
    );
    if (formControl) formControl.formSubmittedValue = false;
  }

  // Runs on every text change. Updates the "X / 3000" counter display and
  // applies color styling: red when over limit, yellow when approaching, muted otherwise.
  // Guards against the editor or counter target not being available yet.
  updateCount () {
    if (!this.editorElement?.editor || !this.hasCounterTarget) return;

    const currentLength = this.editorElement.editor.getDocument().toString().trim().length;
    const remaining = this.maxValue - currentLength;
    this.isOverLimit = remaining < 0;

    this.counterTarget.textContent = `${currentLength} / ${this.maxValue}`;
    this.counterTarget.classList.remove("text-red-600", "font-semibold", "text-yellow-600", "ts-text-subdued");

    if (this.isOverLimit) {
      this.counterTarget.classList.add("text-red-600", "font-semibold");
    } else {
      if (this.hasErrorTarget) this.errorTarget.classList.add("hidden");
      if (remaining <= WARNING_THRESHOLD) {
        this.counterTarget.classList.add("text-yellow-600");
      } else {
        this.counterTarget.classList.add("ts-text-subdued");
      }
    }
  }
}
